import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

function GoogleButton() {
  const { t } = useLang();
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button data-testid="google-login-btn" type="button" onClick={onClick} variant="outline" className="w-full rounded-full h-12 border-2 border-zinc-200 hover:border-zinc-400 bg-white text-zinc-900 font-semibold">
      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {t("auth.continueGoogle")}
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success(t("auth.welcomeToast"));
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="login-page" className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-3xl border border-zinc-100 p-8 md:p-10">
        <h1 className="font-display font-extrabold text-3xl tracking-tighter text-zinc-900">{t("auth.welcomeBack")}</h1>
        <p className="mt-2 text-sm text-zinc-500">{t("auth.welcomeSub")}</p>

        <div className="mt-8">
          <GoogleButton />
          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-400">
            <div className="flex-1 h-px bg-zinc-200" /> {t("auth.orEmail")} <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-zinc-800">{t("auth.email")}</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-zinc-800">{t("auth.password")}</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={submitting} className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12 text-base font-semibold">
              {submitting ? t("auth.signingIn") : t("auth.signIn")}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center text-sm text-zinc-500">
          {t("auth.newHere")}{" "}
          <Link data-testid="to-register-link" to="/register" className="font-semibold text-brand hover:underline">{t("auth.createAccount")}</Link>
        </div>
      </div>
    </main>
  );
}
